import {
  compact,
  every,
  filter,
  find,
  get,
  includes,
  isEmpty,
  map,
  pickBy,
  size,
  some,
} from "lodash";
import {
  auth0AccountSessionSelector,
  hasBothPreTaxAndRothFundsSelector,
  hasPreTaxFundsSelector,
  hasRothFundsSelector,
  paychexPerksSelector,
} from "./auth0";
import { isBankVerifiedSelector } from "./bank";
import { perksEnabled } from "utils/perksEnabled";
import {
  AccountActive,
  IndividualLoginCreated,
  individualPreKycSubmissionStates,
  lifecycleStates,
  onboardingStates,
} from "statics/states";
import { isSplitEligibleWorkerRecord } from "utils/paychexWorkerRecords";
import type { PaychexUserLinkType } from "statics/paychexUserLinkTypes";
import {
  paychexUserLinkTypes,
  perksUserLinkTypes,
  rolloverUserLinkTypes,
} from "statics/paychexUserLinkTypes";
import { ROTH_IRA, TRADITIONAL_IRA } from "statics/accountTypes";
import moment from "moment-timezone";
import { investmentAccountsSelector } from "./accounts";
import type { RootState } from "types/state";
import type {
  PaychexSplitContribution,
  PaychexWorkerRecord,
} from "types/models/paychexWorkerRecord";

const EMPLOYER_ROLE_ID = "1";
const ADMIN_ROLE_ID = "2";
const INDIVIDUAL_ROLE_ID = "3";
const ICON_ADMIN_ROLE_ID = "4";

export const canSetupContributionSelector = (state: RootState) => {
  const hasPortfolio =
    (get(state.user, "userState.state", "") as string) !==
    "IndividualPortfolioPending";
  const hasBankAccount = isBankVerifiedSelector(state);
  const isSelfEmployed =
    isEmpty(state.user.employerLinkRequests) ||
    every(
      state.user.employerLinkRequests,
      (link) => link.status === "REJECTED"
    );

  if (!isSelfEmployed && !hasPortfolio) {
    return false;
  } else if (!isSelfEmployed && hasPortfolio) {
    return true;
  }
  return hasBankAccount && hasPortfolio;
};

export const userIsLockedSelector = (state: RootState) => {
  return userIsLocked(get(state, "user.userState.state", ""));
};

export const userIsClosingAccountSelector = (state: RootState) => {
  const userState = get(state, "user.userState.state") as string | undefined;
  const lockedStates = ["IndividualClosePending", "IndividualClosed"];
  return includes(lockedStates, userState);
};

interface UserProfileLike {
  state?: string;
  employeeId?: string;
}

export const userProfilesSelector = (state: RootState) => {
  // employer slice is still untyped — cast at the boundary
  const userProfiles = get(state, "employer.userProfiles", {}) as Record<
    string,
    UserProfileLike
  >;
  return pickBy(userProfiles, (value) => !userIsLocked(value.state));
};

export const lockedUserProfilesSelector = (state: RootState) => {
  const userProfiles = get(state, "employer.userProfiles", {}) as Record<
    string,
    UserProfileLike
  >;
  return pickBy(userProfiles, (value) => userIsLocked(value.state));
};

export const usersWithNoContributionChangeSelector = (state: RootState) => {
  return filterOutLockedUserProfiles(
    state,
    "payroll.contributionsWithNoChange"
  );
};

export const usersWithPendingContributionsSelector = (state: RootState) => {
  return filterOutLockedUserProfiles(
    state,
    "payroll.pendingContributionChanges"
  );
};

export const isEmployerAdminSelector = (state: RootState) => {
  return get(state, "user.userRoleId") === EMPLOYER_ROLE_ID;
};

export const isEmployerAgentSelector = (state: RootState) => {
  return get(state, "user.userRoleId") === ADMIN_ROLE_ID;
};

export const isEmployerSelector = (state: RootState) => {
  return isEmployerAdminSelector(state) || isEmployerAgentSelector(state);
};

export const isIndividualSelector = (state: RootState) => {
  return get(state, "user.userRoleId") === INDIVIDUAL_ROLE_ID;
};

export const isIconAdminSelector = (state: RootState) => {
  return get(state, "user.userRoleId") === ICON_ADMIN_ROLE_ID;
};

export const pendingRequiredUpdates = (state: RootState) => {
  // pre-existing: the reducer writes requiredUpdates.updates, never
  // .pendingSteps — this selector has always returned the [] default
  // (recorded in the migration doc)
  return get(state, "user.requiredUpdates.pendingSteps", []) as unknown[];
};

export const getIraAccountIdSelector = (
  state: RootState
): string | null | undefined => {
  const accounts = investmentAccountsSelector(state);

  if (isEmpty(accounts)) {
    return null;
  }

  if (size(accounts) === 1) {
    return get(accounts, "[0].id");
  }

  const activeAccountId = auth0AccountSessionSelector(state);
  const activeAccountFromId = accounts.find(
    (account) => account.id === activeAccountId
  );

  const accountId = get(activeAccountFromId, "id");
  return accountId;
};

function filterOutLockedUserProfiles(
  state: RootState,
  userProfilePath: string
) {
  const userIdToUserMap = get(state, userProfilePath) as Record<
    string,
    UserProfileLike
  >;
  const lockedUserProfiles = lockedUserProfilesSelector(state);
  return pickBy(
    userIdToUserMap,
    (value) => !lockedUserProfiles[value.employeeId as string]
  );
}

function userIsLocked(userState?: string) {
  const lockedStates = [
    "IndividualLocked",
    "IndividualContributionPendingLocked",
    "IndividualPortfolioPendingLocked",
    "IndividualDormantLocked",
    "IndividualInvestmentProfileUpdateLocked",
    "IndividualPortfolioUpdateLocked",
    "IndividualRequiredUpdatesPendingLocked",
  ];
  return includes(lockedStates, userState);
}

export const isPreKycSubmissionState = (state: RootState) => {
  const userState = get(state, "user.userState.state") as string | undefined;
  return includes(individualPreKycSubmissionStates, userState);
};

export const userProfile = (state: RootState) => {
  const userProfile = get(state, "user.profile", {});
  return userProfile;
};

export const userEmail = (state: RootState) => {
  const userEmail = get(state, `user.auth0User.email`) as string | undefined;

  return userEmail;
};

export const activeIraAccount = (state: RootState) => {
  const accounts = investmentAccountsSelector(state);
  const activeAccountId = getIraAccountIdSelector(state);

  const activeAccount = isEmpty(activeAccountId)
    ? get(accounts, "[0]")
    : find(accounts, { id: activeAccountId });

  return activeAccount;
};

export const isIndividualAccountOverview = (state: RootState) => {
  const accounts = investmentAccountsSelector(state);
  const activeAccountId = getIraAccountIdSelector(state);
  const accountOverview = isEmpty(activeAccountId) && size(accounts) >= 2;

  return accountOverview;
};

export const isRolloverSelector = (state: RootState) => {
  const accounts = investmentAccountsSelector(state);
  const activeAccountId = getIraAccountIdSelector(state);
  const userHasARollover = some(accounts, {
    id: activeAccountId,
    isRollover: true,
  });

  return userHasARollover;
};

export const activeAccountState = (state: RootState) => {
  const accounts = investmentAccountsSelector(state);
  const activeAccountId = getIraAccountIdSelector(state);
  const activeAccount = find(accounts, { id: activeAccountId });

  const accountState = activeAccount ? activeAccount.state : null;

  return accountState;
};

export const individualIsPastSignupSelector = (state: RootState) =>
  // user.userState is typed `object` on the slice; same boundary cast as PrivateRoute
  includes(lifecycleStates, get(state.user, "userState.state", "") as string);

// During signup a member and their first account move together, so a member past
// signup with a mid-application account can only be on a subsequent one.
export const isOnboardingSubsequentAccountSelector = (state: RootState) =>
  individualIsPastSignupSelector(state) &&
  includes(onboardingStates, activeAccountState(state) as string);

export const activeAccountType = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;

  if (isEmpty(accounts)) {
    return null;
  }

  const activeAccount = get(state.accounts.iraAccounts, "0");
  const activeAccountId = auth0AccountSessionSelector(state);

  const activeAccountFromId = accounts.find(
    (account) => account.id === activeAccountId
  );

  const getActiveAccountById = get(activeAccountFromId, "accountType");
  const accountTypeFromActiveAccount = activeAccount.accountType;

  const accountType = isEmpty(activeAccountId)
    ? accountTypeFromActiveAccount
    : getActiveAccountById;

  return accountType;
};

export const newActiveRolloverAccount = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;

  if (accounts.length !== 2) {
    return false;
  }

  const hasActiveRolloverAccount = some(accounts, {
    isRollover: true,
  });
  const hasRegularAccount = some(accounts, {
    isRollover: false,
  });

  const newRolloverAccount = hasActiveRolloverAccount && hasRegularAccount;
  return newRolloverAccount;
};

export const activeRolloverAccountSameAsCurrent = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;
  const activeAccountId = auth0AccountSessionSelector(state);
  const activeAccount = filter(accounts, { id: activeAccountId });

  const rolloverAccountSameAsCurrent = some(activeAccount, {
    lifecycleState: "AccountActive",
    isRollover: true,
  });
  return rolloverAccountSameAsCurrent;
};

export const activeRolloverIsMixed = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;
  const rolloverAccounts = filter(accounts, { isRollover: true });

  if (rolloverAccounts.length !== 2) {
    return false;
  }

  const hasActiveAccount = some(rolloverAccounts, {
    lifecycleState: "AccountActive",
  });

  const mixedRollover = hasActiveAccount;
  return mixedRollover;
};

export const isProcessingRolloverSelector = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;
  const isProcessing = some(accounts, {
    isProcessingRollover: true,
  });

  return isProcessing;
};

export const activeIdIsProcessingRollover = (state: RootState) => {
  const accounts = state.accounts.iraAccounts;
  const activeAccountId = getIraAccountIdSelector(state);
  const activeAccount = find(accounts, { id: activeAccountId });

  if (!activeAccount) {
    return false;
  }

  const accountIsProcessing = get(activeAccount, "isProcessingRollover", false);
  return accountIsProcessing;
};

interface UserAuthorizationLike {
  identityProvider?: string;
}

export const userHasMoreThanOneAuth = (state: RootState) => {
  const userAuthorizations = get(
    state,
    "user.userAuthorizations",
    []
  ) as UserAuthorizationLike[];
  return userAuthorizations.length > 1;
};

export const userIs401kRollIn = (state: RootState) => {
  const userAuthorizations = get(
    state,
    "user.userAuthorizations",
    []
  ) as UserAuthorizationLike[];
  const rollInPartnersSSO = ["PAYCHEX_OAUTH"];
  return some(userAuthorizations, (auth) => {
    return includes(rollInPartnersSSO, auth.identityProvider);
  });
};

export const userIs401kRollInPaychex = (state: RootState) => {
  const userAuthorizations = get(
    state,
    "user.userAuthorizations",
    []
  ) as UserAuthorizationLike[];
  const paychexProviderId = "PAYCHEX_OAUTH";
  return some(userAuthorizations, {
    identityProvider: paychexProviderId,
  });
};

// Paychex Flex (Perks) — distinct from the existing PAYCHEX_OAUTH 401k roll-in.
// The entry signal is a namespaced custom claim on the Auth0 ID token (set from
// the user's appMetadata by an Auth0 Action); see paychexPerksSelector.
export const userIsPaychexPerks = (state: RootState) =>
  paychexPerksSelector(state);

// Which Paychex product(s) the user holds, from the platform link state
// (UserState.paychexLinkType). Null means unknown — either not a Paychex user or
// the platform lookup failed.
export const paychexLinkTypeSelector = (state: RootState) => {
  if (!perksEnabled()) {
    return null;
  }
  return get(state, "user.paychexLinkType", null) as PaychexUserLinkType | null;
};

// A person can hold a 401k roll-in, Perks, or both, so these two are
// independent — neither is the negation of the other. When linkType is unknown
// they fall back to the pre-linkType inference (userIs401kRollIn only means
// "arrived via Paychex SSO", which is true of both products) so a platform
// outage degrades to the old behavior instead of dropping product UI.
export const userHasPaychexRollover = (state: RootState) => {
  const linkType = paychexLinkTypeSelector(state);
  if (linkType === null) {
    return userIs401kRollIn(state) && !userIsPaychexPerks(state);
  }
  return includes(rolloverUserLinkTypes, linkType);
};

export const userHasPaychexPerksLink = (state: RootState) => {
  const linkType = paychexLinkTypeSelector(state);
  if (linkType === null) {
    return userIsPaychexPerks(state);
  }
  return includes(perksUserLinkTypes, linkType);
};

export const userIsPaychexRolloverOnly = (state: RootState) =>
  paychexLinkTypeSelector(state) === paychexUserLinkTypes.ROLLOVER;

export const isPaychexSubsequentAccountSelector = (state: RootState) =>
  userHasPaychexRollover(state) && isOnboardingSubsequentAccountSelector(state);

// --- Paychex rollover invites -----------------------------------------------
//
// The has_pretax_funds / has_roth_funds Auth0 claims describe the *401(k)* and
// never change — not even after an account opens — so they establish eligibility
// only. Account-type coverage is what says whether a rollover is outstanding.

const FAILED_ROLLOVER_STATUSES = ["CANCELLED", "EXPIRED", "ERROR"];

export type UnopenedRolloverAccountType =
  | typeof TRADITIONAL_IRA
  | typeof ROTH_IRA;

/**
 * The rollover account type a mixed-funds Paychex roll-in user has not opened
 * yet — someone who took the single-IRA path at onboarding
 * (Individual401kPartialRollover) — or null when the invite does not apply.
 *
 * A non-AccountActive account suppresses it: the member is mid-application (a
 * second application would stall behind it), or holds a closed/locked account,
 * and either way an upsell is the wrong thing on screen.
 */
export const unopenedPaychexRolloverTypeSelector = (
  state: RootState
): UnopenedRolloverAccountType | null => {
  if (!userHasPaychexRollover(state)) {
    return null;
  }

  if (!hasBothPreTaxAndRothFundsSelector(state)) {
    return null;
  }

  const accounts = state.accounts.iraAccounts;

  if (
    isEmpty(accounts) ||
    !every(accounts, { lifecycleState: AccountActive })
  ) {
    return null;
  }

  // A roll-in member has no other way to hold an Icon account, so the account is
  // itself the evidence of the roll-in — the rollover records lag it, and for a
  // partial rollover never land. Someone who also holds Perks may be sitting on a
  // paycheck-split IRA instead, so for them the records stay the proof.
  if (userHasPaychexPerksLink(state)) {
    const rollovers = compact(
      accounts.flatMap((account) => account.rollovers ?? [])
    );

    if (isEmpty(rollovers)) {
      return null;
    }

    if (
      some(rollovers, (rollover) =>
        includes(FAILED_ROLLOVER_STATUSES, rollover.status)
      )
    ) {
      return null;
    }
  }

  const openTypes = map(accounts, "accountType");

  if (!includes(openTypes, TRADITIONAL_IRA)) {
    return TRADITIONAL_IRA;
  }

  if (!includes(openTypes, ROTH_IRA)) {
    return ROTH_IRA;
  }

  return null;
};

/**
 * The rollover account types a Paychex user has 401(k) funds for but holds no
 * account for yet — the Perks -> 401(k) case, where someone already active at
 * Icon comes back from the rollover tile and linkPaychexRolloverUser writes
 * their fund claims.
 *
 * Rolling into a type they already hold needs no account and no UI: the link
 * call alone tells Paychex where to send it. Returns [] once any rollover
 * account exists — unopenedPaychexRolloverTypeSelector owns the flow from there.
 */
export const unopenedFirstRolloverTypesSelector = (
  state: RootState
): UnopenedRolloverAccountType[] => {
  if (!userHasPaychexRollover(state)) {
    return [];
  }

  const accounts = state.accounts.iraAccounts;

  if (
    isEmpty(accounts) ||
    !every(accounts, { lifecycleState: AccountActive })
  ) {
    return [];
  }

  if (some(accounts, { isRollover: true })) {
    return [];
  }

  const openTypes = map(accounts, "accountType");
  const types: UnopenedRolloverAccountType[] = [];

  if (hasPreTaxFundsSelector(state) && !includes(openTypes, TRADITIONAL_IRA)) {
    types.push(TRADITIONAL_IRA);
  }

  if (hasRothFundsSelector(state) && !includes(openTypes, ROTH_IRA)) {
    types.push(ROTH_IRA);
  }

  return types;
};

export const paychexRolloverIsPendingSelector = (state: RootState) =>
  some(state.accounts.iraAccounts, (account) =>
    some(account.rollovers, { status: "PENDING" })
  );

export const paychexRolloverIsCompleteSelector = (state: RootState) =>
  some(state.accounts.iraAccounts, (account) =>
    some(account.rollovers, { status: "COMPLETE" })
  );

// The unfiltered list — only the split-eligibility questions below should read
// it; the UI itself reads paychexWorkerRecordsSelector.
export const allPaychexWorkerRecordsSelector = (state: RootState) =>
  get(state, "user.paychexWorkerRecords", []) as PaychexWorkerRecord[];

export const paychexWorkerRecordsSelector = (state: RootState) =>
  filter(allPaychexWorkerRecordsSelector(state), isSplitEligibleWorkerRecord);

export const hasPaychexEmployeeWorkerRecords = (state: RootState) =>
  !isEmpty(paychexWorkerRecordsSelector(state));

export const userIsPaychexSplitIneligible = (state: RootState) =>
  userIsPaychexPerks(state) &&
  !isEmpty(allPaychexWorkerRecordsSelector(state)) &&
  !hasPaychexEmployeeWorkerRecords(state);

// The full record for the worker the person selected from the list — carries the
// resolved payrollFrequency (+ companyId) that the list query leaves null.
export const selectedPaychexWorkerRecordSelector = (state: RootState) =>
  get(
    state,
    "user.selectedPaychexWorkerRecord",
    null
  ) as PaychexWorkerRecord | null;

// The person's active PAYCHECK_SPLIT contribution (v1: at most one), merged with
// its schedule — or null when none is set up. Mirrors how recurringContribution
// is read; maps back to its worker record by companyId.
export const paycheckSplitContributionSelector = (state: RootState) =>
  get(
    state,
    "user.paycheckSplitContribution",
    null
  ) as PaychexSplitContribution | null;

export const getUserAgreementDate = (
  state: RootState,
  agreementType: string
) => {
  const agreements = get(state, "user.userAgreements", []) as Array<{
    type?: string;
    agreementDate?: string;
  }>;
  const agreement = find(agreements, { type: agreementType });
  if (!agreement) {
    return "";
  }
  const acceptanceDate = get(agreement, "agreementDate", "");
  return moment
    .utc(acceptanceDate)
    .tz("America/New_York")
    .format("MMMM D, YYYY");
};

export const getUserPassportAgreementDate = (state: RootState) => {
  return getUserAgreementDate(state, "passport_agreement");
};

export const getUserCRBAgreementDate = (state: RootState) => {
  return getUserAgreementDate(state, "crb_agreement");
};

export const getUserVirtualAccountNumberDisclosureDate = (state: RootState) => {
  return getUserAgreementDate(state, "virtual_account_number_disclosure");
};

export const userPendingEmailVerification = (state: RootState) => {
  const userState = get(state, "user.userState.state") as string | undefined;
  return userState === IndividualLoginCreated;
};

export const totalAccountBalance = (state: RootState) => {
  const accountId = getIraAccountIdSelector(state);
  const accounts = state.accounts.iraAccounts;
  const selectedAccount = find(accounts, { id: accountId });
  const totalAccountBalance = get(
    selectedAccount,
    "balances.totalAccountBalance",
    0
  );
  return totalAccountBalance;
};
